// AppShellHost — the library/world load-effect bundle (C1 decomposition, #9).
//
// Extracted verbatim from AppShellHost.tsx: the ~20 sanctioned port-call
// `useEffect`s that project the world into shell state — the auth probe, the
// template catalogue, learning contexts, the History/Finder overlay reads, the
// library load (+ tab reconcile + boot-health notice), the connectivity
// heartbeat, the cold-return marker, the working-set persist, the active
// source/export/material detail loads, the
// explicit/ambient context reads, the conversation-summary + transcript loads,
// and the per-material policy cache mirror/lazy-load.
//
// This hook CALLS `useEffect` (unlike the pure handler-factory hooks): it must
// be invoked at the SAME position AppShellHost previously inlined these effects
// (right after `useConnectionHealth`, before the object-nav hub) so the effects
// keep their declaration order relative to every sibling hook. Each effect body
// is unchanged, dep arrays and biome-ignore pragmas carried verbatim — the only
// change is that the setters/reads/handlers it closes over now arrive as `deps`.

import * as Core from "@bilda/core";
import { Remote, Result } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import {
    type Dispatch,
    type MutableRefObject,
    type SetStateAction,
    useEffect,
} from "react";
import type { AmbientContextItem } from "../../components/Chat/types.js";
import type { ComposerResourceRef } from "../../components/Composer/PowerComposer/types.js";
import type { LoadedProposal } from "../../components/Editor/ProposalReview/ProposalReview.js";
import type { EditorHostApi } from "../../components/EditorHost/EditorHost.js";
import type { FinderDoc } from "../../components/Finder/finderIndex.js";
import type { Health } from "../../components/Health/ConnectionIndicator.js";
import type {
    TemplateCatalog,
    TemplateChoice,
} from "../../components/MaterialCreate/MaterialCreate.js";
import type { PaneTree } from "../../components/Navigation/paneTree.js";
import type {
    ConversationId,
    MaterialRef,
    ResourceId,
    ResourceRef,
} from "../../components/Navigation/types.js";
import type { MaterialQuality } from "../../components/QualitySummary/QualitySummary.js";
import type { RecentThread } from "../../components/WorkspaceLanding/WorkspaceLanding.js";
import type { ChatCommand } from "../../modules/chat.js";
import type { ConversationList } from "../conversationSummary.js";
import type { ExternalError } from "../externalError.js";
import type { FrameTransition, HostNotice, Overlay } from "../frame.js";
import { captureVisit } from "../lastVisit.js";
import type {
    AmbientItemDTO,
    AppPorts,
    AuthStatus,
    FileHistoryView,
    HistoryView,
    LearningContextSummary,
    Library,
    LoadedExport,
    LoadedMaterial,
    LoadedSource,
} from "../ports.js";
import {
    type LogHead,
    shouldRefetchState,
    singleFlight,
} from "../stateHeartbeat.js";
import { writeViewState } from "../viewState.js";
import {
    AMBIENT_CONVERSATION_ID,
    HEARTBEAT_MS,
    refsOfLibrary,
    toComposerResourceRef,
} from "./constants.js";
import { editedMaterialVersion } from "./editedMaterial.js";
import { rowVersion } from "./liveLibrary.js";

export type LibraryLoadDeps = {
    readonly ports: AppPorts;
    readonly setAuthStatus: Dispatch<SetStateAction<AuthStatus | null>>;
    readonly setTemplateCatalog: Dispatch<SetStateAction<TemplateCatalog>>;
    readonly setLearningContexts: Dispatch<
        SetStateAction<readonly LearningContextSummary[]>
    >;
    readonly overlay: Overlay;
    readonly setFileHistory: Dispatch<
        SetStateAction<{
            readonly resourceKey: string;
            readonly view: FileHistoryView;
        } | null>
    >;
    readonly setHistoryVersionId: Dispatch<SetStateAction<string | null>>;
    readonly setHistoryView: Dispatch<SetStateAction<HistoryView | null>>;
    readonly setFinderDocs: Dispatch<SetStateAction<readonly FinderDoc[]>>;
    readonly setFinderLoading: Dispatch<SetStateAction<boolean>>;
    readonly setLibraryError: Dispatch<SetStateAction<ExternalError | null>>;
    readonly foldReadHealth: (ok: boolean) => void;
    readonly setLibrary: Dispatch<SetStateAction<Library | null>>;
    readonly dispatch: Dispatch<FrameTransition>;
    readonly showNotice: (n: HostNotice) => void;
    readonly isTransient: (e: ExternalError) => boolean;
    readonly retrySeq: number;
    readonly bootHealthSigRef: MutableRefObject<string>;
    readonly mkConversationId: () => ConversationId;
    readonly health: Health;
    readonly authStatus: AuthStatus | null;
    readonly paneTree: PaneTree;
    // The open project (frame.studioProject), kept with the pane tree.
    readonly studioProject: string | null;
    readonly activeSourceId: string | null;
    readonly sourceReloadKey: number;
    readonly setSourceError: Dispatch<SetStateAction<ExternalError | null>>;
    readonly setSourceDetail: Dispatch<SetStateAction<LoadedSource | null>>;
    readonly activeExportId: string | null;
    readonly setExportDetail: Dispatch<SetStateAction<LoadedExport | null>>;
    readonly setExportError: Dispatch<SetStateAction<ExternalError | null>>;
    readonly activeContextAnchor: Core.EntityRef.Ref | null;
    readonly activeContextAnchorKey: string | null;
    readonly library: Library | null;
    readonly setActiveContextItems: Dispatch<
        SetStateAction<readonly ComposerResourceRef[]>
    >;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly setActiveAmbientItems: Dispatch<
        SetStateAction<readonly AmbientContextItem[]>
    >;
    readonly resolveAmbientItems: (
        dto: readonly AmbientItemDTO[],
    ) => readonly AmbientContextItem[];
    readonly activeConversationId: string;
    readonly setConversationList: Dispatch<
        SetStateAction<ConversationList<RecentThread, ExternalError>>
    >;
    readonly conversationListSeq: number;
    readonly visibleConversationIds: readonly string[];
    readonly visibleConversationIdsKey: string;
    readonly sendChat: (command: ChatCommand) => void;
    readonly activeMaterialId: string | null;
    readonly activeMaterialRef: MaterialRef | null;
    readonly loadedMaterialRef: MutableRefObject<string | null>;
    readonly editor: EditorHostApi;
    readonly setQuality: Dispatch<SetStateAction<MaterialQuality | null>>;
    readonly setPolicy: Dispatch<
        SetStateAction<Core.Policy.Evaluate.PolicyEvaluation | null>
    >;
    readonly setActiveMaterialTemplate: Dispatch<
        SetStateAction<{
            readonly id: string;
            readonly version: number;
        } | null>
    >;
    readonly setPaneSubject: Dispatch<SetStateAction<MaterialRef | null>>;
    readonly policy: Core.Policy.Evaluate.PolicyEvaluation | null;
    readonly activeQualityMaterialId: string | null;
    readonly setPolicyCache: Dispatch<
        SetStateAction<
            ReadonlyMap<string, Core.Policy.Evaluate.PolicyEvaluation>
        >
    >;
};

export const useLibraryLoad = ({
    ports,
    setAuthStatus,
    setTemplateCatalog,
    setLearningContexts,
    overlay,
    setFileHistory,
    setHistoryVersionId,
    setHistoryView,
    setFinderDocs,
    setFinderLoading,
    setLibraryError,
    foldReadHealth,
    setLibrary,
    dispatch,
    showNotice,
    isTransient,
    retrySeq,
    bootHealthSigRef,
    mkConversationId,
    health,
    authStatus,
    paneTree,
    studioProject,
    activeSourceId,
    sourceReloadKey,
    setSourceError,
    setSourceDetail,
    activeExportId,
    setExportDetail,
    setExportError,
    activeContextAnchor,
    activeContextAnchorKey,
    library,
    setActiveContextItems,
    refFor,
    setActiveAmbientItems,
    resolveAmbientItems,
    activeConversationId,
    setConversationList,
    conversationListSeq,
    visibleConversationIds,
    visibleConversationIdsKey,
    sendChat,
    activeMaterialId,
    activeMaterialRef,
    loadedMaterialRef,
    editor,
    setQuality,
    setPolicy,
    setActiveMaterialTemplate,
    setPaneSubject,
    policy,
    activeQualityMaterialId,
    setPolicyCache,
}: LibraryLoadDeps): void => {
    const { t } = useLingui();
    const untitled = t`Untitled`;
    // The auth probe: project `/auth/me` into `authStatus` so the sign-in gate
    // below is a deterministic read of state, not a reaction buried in a fetch.
    // An UNREADABLE probe decides nothing: a server mid-restart (the dev
    // server's watch restart, a deploy gap — the Vite proxy answers 500) is not
    // a signed-out author, and projecting `anonymous` from it dropped a
    // signed-in author on the public landing at every reload. Instead a
    // transient failure goes offline through the shared backoff, whose tick
    // (`retrySeq`) re-runs this probe until it reads; a 401 on the guarded
    // `/state` load still flips to `anonymous`. Once signed in, it stops.
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only on the backoff tick; `authStatus` is read as a guard, and the setters/folds are stable or ref-backed.
    useEffect(() => {
        if (authStatus?._tag === "authenticated") return;
        let cancelled = false;
        const run = async () => {
            const r = await ports.loadAuthStatus();
            if (cancelled) return;
            if (Result.isOk(r)) setAuthStatus(r.value);
            else if (isTransient(r.error)) foldReadHealth(false);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, retrySeq]);

    // The template catalogue feeding the creation dialog, once on mount. A
    // failure renders the dialog's honest unavailable state — never an
    // empty picker pretending there is nothing to pick.
    // biome-ignore lint/correctness/useExhaustiveDependencies: dep array preserved verbatim from AppShellHost (C1 #9); the omitted deps are React-stable setters, intentionally not listed.
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const r = await ports.loadTemplates();
            if (cancelled) return;
            Result.match<readonly TemplateChoice[], unknown, void>({
                ok: (templates) =>
                    setTemplateCatalog({ type: "loaded", templates }),
                err: () => setTemplateCatalog({ type: "unavailable" }),
            })(r);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports]);

    // The workspace's audiences ("who this is for"), read again whenever the
    // library is (the log moved: an audience framed here, in another tab or by
    // the assistant). A failure keeps the list shown; a list never read stays
    // empty, so the bar degrades to the "frame an audience" invitation rather
    // than a crash.
    // biome-ignore lint/correctness/useExhaustiveDependencies: `library` is the re-read signal (the body does not read it); the setter is React-stable.
    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            const r = await ports.loadLearningContexts();
            if (cancelled) return;
            if (Result.isOk(r)) setLearningContexts(r.value);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, library]);

    // Load the History & Inspection surface's data when its overlay opens. Over
    // HTTP the read is unavailable yet (the A.History fold gap) — it errors and
    // the overlay shows a calm empty state; the mock serves fixtures. The
    // drilled file-history clears when the surface closes.
    // biome-ignore lint/correctness/useExhaustiveDependencies: dep array preserved verbatim from AppShellHost (C1 #9); the omitted deps are React-stable setters, intentionally not listed.
    useEffect(() => {
        if (overlay.type !== "history") {
            setFileHistory(null);
            setHistoryVersionId(null);
            return;
        }
        let cancelled = false;
        const run = async () => {
            const r = await ports.loadHistory();
            if (cancelled) return;
            setHistoryView(Result.isOk(r) ? r.value : null);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, overlay.type]);

    // Load the content-finder's corpus when its overlay opens (.claude/analysis/
    // fuzzy-finder.md §7: on-demand scan, no persisted index) — workspace-wide
    // now, no project to scope it by. A failed read leaves the finder
    // searching titles only — the last-good docs stay; never a crash. Cleared
    // when the finder closes.
    // biome-ignore lint/correctness/useExhaustiveDependencies: dep array preserved verbatim from AppShellHost (C1 #9); the omitted deps are React-stable setters, intentionally not listed.
    useEffect(() => {
        if (overlay.type !== "finder") {
            setFinderDocs([]);
            setFinderLoading(false);
            return;
        }
        let cancelled = false;
        setFinderLoading(true);
        const run = async () => {
            const r = await ports.loadFinderIndex();
            if (cancelled) return;
            if (Result.isOk(r)) setFinderDocs(r.value);
            setFinderLoading(false);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, overlay.type]);

    // Project a freshly loaded library into the shell: the library itself, the
    // reconciled working set, and the boot-health notice. Shared by the load
    // effect and the heartbeat's head-changed refetch.
    const applyLibrary = (lib: Library) => {
        setLibrary(lib);
        // Reconcile the (possibly restored) working set against
        // the loaded truth: stale tabs drop, titles refresh
        // (U46 graceful degradation — never a crash).
        dispatch({
            type: "reconcile-tabs",
            refs: refsOfLibrary(lib, untitled),
            freshConversationId: mkConversationId(),
        });
        // Aggregate-scoped replay degradation (ADR-3): raise the
        // calm notice once per changed report. `showNotice` is the
        // same seam every other host outcome uses.
        const bh = lib.bootHealth;
        const sig = `${bh.frozenAggregates}:${bh.unroutableEvents}`;
        if (sig !== bootHealthSigRef.current) {
            bootHealthSigRef.current = sig;
            if (bh.frozenAggregates > 0 || bh.unroutableEvents > 0) {
                showNotice({
                    type: "replay-degraded",
                    frozen: bh.frozenAggregates,
                    unroutable: bh.unroutableEvents,
                });
            }
        }
    };

    // The workspace's library, once on mount and on every re-read signal
    // (retrySeq / offline backoff) — the whole flat pool, no project to
    // switch between.
    // biome-ignore lint/correctness/useExhaustiveDependencies: retrySeq IS the re-read signal; the body deliberately doesn't read it.
    useEffect(() => {
        let cancelled = false;
        setLibraryError(null);
        const run = async () => {
            const r = await ports.loadLibrary();
            if (cancelled) return;
            Result.match<Library, ExternalError, void>({
                ok: (lib) => {
                    foldReadHealth(true);
                    applyLibrary(lib);
                },
                // A 401 from /state is an auth lapse, not a load fault: flip the
                // projected status to anonymous so the render drops to the
                // sign-in gate (with the stale cookie now cleared server-side)
                // instead of the "session expired — reload" doom loop. Transient
                // → keep the shell (the last-good library stays) + health offline
                // + backoff retry; any other terminal error → the full screen.
                err: (e) => {
                    if (e._tag === "External.Unauthorized") {
                        setAuthStatus({ _tag: "anonymous" });
                        return;
                    }
                    isTransient(e) ? foldReadHealth(false) : setLibraryError(e);
                },
            })(r);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, retrySeq, untitled]);

    // Cold-open rests on the CALM EMPTY LANDING (home). The decision lives in
    // the pure resting state (`emptyTabs` / the restored working set), so there
    // is no cold-open effect to run — a fresh tab is one Cmd+T / "+" away.

    // Connectivity heartbeat (offline round Stage A): while online, ping the
    // server periodically so a mid-session disconnect flips the health chip
    // WITHOUT the author having to trigger a read. Once offline, this effect
    // stops (the backoff retry loop takes over) and
    // resumes when health returns online. A terminal error is not a
    // connectivity fault, so only a transient failure flips the chip.
    // The ping is the cheap event-log head, not `/state`; the library is
    // refetched only when the head moved (`shouldRefetchState`). The same
    // check runs the moment the server says the log moved (`watchLogHead`:
    // an assistant's tool, a generation job, another tab or machine), so
    // every view projected from the library follows at once; the interval
    // is the fallback while that stream is down. One check at a time
    // (`singleFlight`), so a burst of appends costs at most two.
    // biome-ignore lint/correctness/useExhaustiveDependencies: health gates the interval; sendChat dispatches into the chat runtime; foldReadHealth/isTransient/applyLibrary only touch refs + setters (applyLibrary's one changing input, `untitled`, is listed), so re-subscribing on their identity would needlessly churn the heartbeat.
    useEffect(() => {
        if (health !== "online") return;
        // While the sign-in gate is showing (anonymous session) there is no
        // point pinging a guarded route on a 10s loop — it only 401s. Stop the
        // heartbeat until a session exists; the effect re-subscribes when
        // authStatus changes.
        if (authStatus?._tag === "anonymous") return;
        let cancelled = false;
        let lastHead: LogHead | undefined;
        const tick = singleFlight(async () => {
            const rHead = await ports.loadLogHead();
            if (cancelled) return;
            foldReadHealth(Result.isOk(rHead) || !isTransient(rHead.error));
            // A terms bump mid-session: the server now refuses this person's
            // reads until they accept again — surface it so the root shows
            // the consent gate.
            if (
                Result.isErr(rHead) &&
                rHead.error._tag === "External.PrivacyTermsNotAccepted"
            ) {
                setLibraryError(rHead.error);
                return;
            }
            if (
                Result.isErr(rHead) ||
                !shouldRefetchState(lastHead, rHead.value)
            ) {
                return;
            }
            const rLib = await ports.loadLibrary();
            if (cancelled || Result.isErr(rLib)) return;
            const moved = lastHead !== undefined;
            lastHead = rHead.value;
            applyLibrary(rLib.value);
            // Open transcripts follow too: a turn the server started on its
            // own (FR-3.5) shows here as its steps land.
            if (moved) sendChat({ type: "log_head_moved" });
        });
        const id = setInterval(() => {
            void tick();
        }, HEARTBEAT_MS);
        const unwatch = ports.watchLogHead(() => {
            void tick();
        });
        return () => {
            cancelled = true;
            clearInterval(id);
            unwatch();
        };
    }, [health, ports, authStatus, untitled]);

    // Cold-return marker (P45a): on mount, stamp "now" as this device's
    // last-visit timestamp for the NEXT return. `captureVisit` does the
    // read-then-write once per page-load — idempotent across StrictMode's
    // double-invoke and full remounts. Device-local (localStorage), never an
    // event. The frozen baseline it returns fed the retired Overview panel's
    // cold-return band; the write stays so the marker keeps advancing for the
    // day that band finds a new home on the landing.
    useEffect(() => {
        captureVisit();
    }, []);

    // Persist the working set (U46): every change to the pane tree writes
    // through to the device — the same sanctioned localStorage effect as the
    // theme. Dirty marks are excluded inside `writeViewState` (unsaved
    // content does not survive a reload).
    useEffect(() => {
        writeViewState({ paneTree, studioProject });
    }, [paneTree, studioProject]);

    // The active source tab's view detail. `sourceReloadKey` re-runs the read
    // after a summary/note command, and so does the source's library row
    // moving (`rowVersion`: written elsewhere); the loaded detail is kept on screen while
    // the re-read is in flight (only a genuine source switch shows the
    // spinner, because the detail was reset by the previous run's cleanup).
    const activeSourceVersion = rowVersion(library, activeSourceId);
    // biome-ignore lint/correctness/useExhaustiveDependencies: sourceReloadKey + activeSourceVersion ARE the re-read signals; the body deliberately doesn't read them.
    useEffect(() => {
        if (activeSourceId === null) return;
        let cancelled = false;
        setSourceError(null);
        const run = async () => {
            const r = await ports.loadSource(activeSourceId);
            if (cancelled) return;
            Result.match<LoadedSource, ExternalError, void>({
                ok: setSourceDetail,
                err: (e) => setSourceError(e),
            })(r);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, activeSourceId, sourceReloadKey, retrySeq, activeSourceVersion]);

    // Reset the loaded detail when the active source itself changes (not on a
    // reload bump) so a switch never shows the previous source's content.
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeSourceId IS the reset signal; the body only clears state.
    useEffect(() => {
        setSourceDetail(null);
    }, [activeSourceId]);

    // The active package tab's view detail, read again when its library row
    // moves; the shown detail stays while it is read again.
    const activeExportVersion = rowVersion(library, activeExportId);
    // biome-ignore lint/correctness/useExhaustiveDependencies: retrySeq + activeExportVersion ARE the re-read signals; the body deliberately doesn't read them.
    useEffect(() => {
        if (activeExportId === null) return;
        let cancelled = false;
        setExportError(null);
        const run = async () => {
            const r = await ports.loadExport(activeExportId);
            if (cancelled) return;
            Result.match<LoadedExport, ExternalError, void>({
                ok: setExportDetail,
                err: (e) => setExportError(e),
            })(r);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, activeExportId, retrySeq, activeExportVersion]);

    // Only another package clears the shown detail, never a re-read.
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeExportId IS the reset signal; the body only clears state.
    useEffect(() => {
        setExportDetail(null);
    }, [activeExportId]);

    // The active tab's EXPLICIT context (context spine step 3a) — reloads
    // whenever the active anchor changes (a tab switch swaps the strip's
    // contents, per the always-visible-strip invariant). No anchor (a
    // Home/Settings/Quality tab is focused) ⇒ nothing to show.
    // `activeContextAnchorKey` (a stable string), not `activeContextAnchor`
    // itself (a fresh object every render), is the re-run signal —
    // `entityRefKey`'s whole point is exactly this. `library` (the whole cell,
    // not just the null→loaded `libraryReady` flip) is the SECOND intended
    // trigger: the port returns bare resource ids that resolve to displayable
    // refs through `refFor` (library-derived), so the projection must re-run
    // whenever the library CONTENT changes, not only on the cold load. This is
    // the F-A post-turn re-read (DB1): `reread_world` bumps `retrySeq`, the
    // library effect reloads and swaps in a NEW `library` value, and keying on
    // that value — not a seq that would fire BEFORE the reload commits — makes
    // the re-projection race-free (the just-created material is in the fresh
    // index before `refFor` runs, so it resolves instead of being dropped).
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeContextAnchorKey + library ARE the re-run signals; activeContextAnchor/refFor are re-derived from the same render's paneTree+library, so reading them in the async body is safe (same pattern as the material-load effect below).
    useEffect(() => {
        if (activeContextAnchor === null) {
            setActiveContextItems([]);
            return;
        }
        // The ids resolve through the library (`refFor`); read before it lands
        // and every id drops. The library's arrival re-runs this effect.
        if (library === null) return;
        let cancelled = false;
        const anchor = activeContextAnchor;
        const run = async () => {
            const r = await ports.loadContext(anchor);
            if (cancelled) return;
            Result.match<readonly string[], ExternalError, void>({
                ok: (resourceIds) =>
                    setActiveContextItems(
                        resourceIds.flatMap((id) => {
                            const ref = refFor(id);
                            return ref === undefined
                                ? []
                                : [toComposerResourceRef(ref)];
                        }),
                    ),
                err: () => setActiveContextItems([]),
            })(r);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, activeContextAnchorKey, library]);

    // The active tab's AMBIENT bloom (context spine step 3b) — parallel to
    // the EXPLICIT effect just above: same re-run signals
    // (`activeContextAnchorKey` + `library`), same no-anchor short-circuit,
    // same graceful-degradation resolve. Re-running on the `library` cell is
    // the F-A post-turn re-read (DB1): a turn that creates a material commits a
    // real `derives-from` KB edge, so once the library reload swaps in the new
    // value the bloom re-fetches AND resolves the material through the fresh
    // `refFor` — before this it stayed at whatever it was when the tab opened
    // (empty). The library moves only when the log did, at most once a second
    // (the head stream's ceiling), so this is never a per-keystroke read.
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeContextAnchorKey + library ARE the re-run signals; activeContextAnchor/refFor are re-derived from the same render's paneTree+library, same pattern as the explicit-context effect above.
    useEffect(() => {
        if (activeContextAnchor === null) {
            setActiveAmbientItems([]);
            return;
        }
        // Same as the explicit read: items resolve through the library.
        if (library === null) return;
        let cancelled = false;
        const anchor = activeContextAnchor;
        const run = async () => {
            const r = await ports.loadAmbientContext(anchor);
            if (cancelled) return;
            Result.match<readonly AmbientItemDTO[], ExternalError, void>({
                ok: (items) =>
                    setActiveAmbientItems(resolveAmbientItems(items)),
                err: () => setActiveAmbientItems([]),
            })(r);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, activeContextAnchorKey, library]);

    // The conversation list (G6): ONE read per boot, re-read on the world
    // re-read (`retrySeq`: a turn's end, offline recovery) and on the rail's
    // own retry (`conversationListSeq`). It used to re-run on every
    // `activeConversationId` change, which at boot fired it twice (the restored
    // selection lands after mount) and on every conversation switch; a started
    // conversation joins via its signal instead. A failure is kept as
    // `Failed`, never collapsed into an empty list; a transient one over a
    // held list keeps the list.
    // biome-ignore lint/correctness/useExhaustiveDependencies: retrySeq + conversationListSeq ARE the re-read signals (not read in-body); activeConversationId is read only for the one-time pre-A3 AMBIENT migration at load; dispatch (useReducer) + mkConversationId + the setter are stable.
    useEffect(() => {
        let cancelled = false;
        setConversationList(Remote.startLoad);
        const run = async () => {
            const result = await ports.listConversations();
            if (cancelled) return;
            if (Result.isErr(result)) {
                // A lost connection keeps the list already shown (the app
                // never disappears); only a list never held, or a real
                // refusal, shows as failed.
                const error = result.error;
                setConversationList((prev) =>
                    prev._tag === "Remote.Reloading" && isTransient(error)
                        ? Remote.loaded(prev.prev)
                        : Remote.failed(error),
                );
                return;
            }
            setConversationList(
                Remote.loaded(
                    result.value.map((item) => ({ ...item, when: "" })),
                ),
            );
            // `ambient` can only arrive from pre-A3 persisted view state.
            // Resolve it immediately to a real aggregate id, then remove the
            // migration placeholder from the working set.
            if (activeConversationId === AMBIENT_CONVERSATION_ID) {
                const existing = result.value[0];
                const resolved =
                    existing === undefined
                        ? await ports.startConversation()
                        : Result.ok(existing);
                if (cancelled || Result.isErr(resolved)) return;
                dispatch({
                    type: "enter-full-chat",
                    conversationId: resolved.value.id,
                });
                dispatch({
                    type: "close-conversation-tab",
                    conversationId: AMBIENT_CONVERSATION_ID,
                    freshConversationId: mkConversationId(),
                });
            }
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, retrySeq, conversationListSeq]);

    // Tell chat which conversations the pane tree shows, not only the focused
    // leaf; chat loads each once per session (a failed load retries on the
    // next report, e.g. after a world re-read), so switching focus between
    // already-visible panes neither blanks nor refetches a transcript.
    // biome-ignore lint/correctness/useExhaustiveDependencies: visibleConversationIdsKey is the stable, deduplicated reload signal; sendChat dispatches into the chat runtime.
    useEffect(() => {
        sendChat({
            type: "conversations_became_visible",
            conversationIds: visibleConversationIds,
        });
    }, [retrySeq, visibleConversationIdsKey]);

    // Load the active material's content whenever it changes: another
    // material, a world re-read, or a new version of this one on its library
    // row (written by the assistant, a generation job, another tab).
    const activeMaterialVersion = editedMaterialVersion(
        library,
        activeMaterialId,
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: retrySeq + activeMaterialVersion ARE the re-read signals (the body deliberately doesn't read them); activeMaterialRef is activeMaterialId's carrier ref — same switch signal, read for the title.
    useEffect(() => {
        if (activeMaterialId === null) return;
        let cancelled = false;
        // Reset the material's working state ONLY on a genuine switch — a
        // same-material re-read (backoff bump while offline) keeps the open
        // material + its undo/loop intact so a disconnect never wipes it
        // (offline round Stage A). loadedMaterialRef advances on success.
        const isMaterialSwitch = loadedMaterialRef.current !== activeMaterialId;
        if (isMaterialSwitch) {
            editor.resetForMaterialSwitch();
            setQuality(null);
            setPolicy(null);
            setActiveMaterialTemplate(null);
        }
        editor.setMaterialError(null);
        // The pane re-projects to THIS material (D7's follow rule): from here
        // the Review/Grounding read is its projection, so the head's subject
        // line appears exactly while some OTHER tab is active.
        setPaneSubject(activeMaterialRef);
        const run = async () => {
            const [contentR, qualityR, policyR, proposalsR] = await Promise.all(
                [
                    ports.loadMaterial(activeMaterialId),
                    ports.evaluateMaterial(activeMaterialId),
                    ports.evaluateMaterialPolicy(
                        activeMaterialId,
                        Core.Policy.Boundary.write(),
                    ),
                    ports.loadProposals(activeMaterialId),
                ],
            );
            if (cancelled) return;
            Result.match<LoadedMaterial, ExternalError, void>({
                ok: (loaded) => {
                    loadedMaterialRef.current = activeMaterialId;
                    editor.applyLoadedMaterial(loaded);
                    setActiveMaterialTemplate(loaded.templateRef ?? null);
                },
                // Transient → keep the open material (a disconnect never blanks
                // it); only a terminal read error shows the material error pane.
                err: (e) => {
                    if (!isTransient(e)) editor.setMaterialError(e);
                },
            })(contentR);
            // The advisory reads (transcript / quality / review queue) clear to
            // empty only on a genuine material switch — a same-material re-read
            // that fails (offline) keeps whatever was already shown.
            Result.match<MaterialQuality, unknown, void>({
                ok: (q) => setQuality(q),
                err: () => {
                    if (isMaterialSwitch) setQuality(null);
                },
            })(qualityR);
            Result.match<Core.Policy.Evaluate.PolicyEvaluation, unknown, void>({
                ok: (p) => setPolicy(p),
                err: () => {
                    if (isMaterialSwitch) setPolicy(null);
                },
            })(policyR);
            Result.match<readonly LoadedProposal[], unknown, void>({
                ok: (ps) => editor.setProposals(ps),
                err: () => {
                    if (isMaterialSwitch) editor.setProposals([]);
                },
            })(proposalsR);
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, activeMaterialId, retrySeq, activeMaterialVersion]);

    // Mirror the active material's freshly-loaded/refreshed policy into the
    // per-material cache, so a full-quality report tab for that material always
    // has its evaluation at hand (even after the author moves to another tab).
    // biome-ignore lint/correctness/useExhaustiveDependencies: dep array preserved verbatim from AppShellHost (C1 #9); setPolicyCache is a React-stable setter, intentionally not listed.
    useEffect(() => {
        if (activeMaterialId === null || policy === null) return;
        setPolicyCache((prev) => {
            if (prev.get(activeMaterialId) === policy) return prev;
            const next = new Map(prev);
            next.set(activeMaterialId, policy);
            return next;
        });
    }, [activeMaterialId, policy]);

    // Read a full-quality report tab's evaluation when the tab opens and again
    // whenever its material's library row moves (the tab can outlive — or
    // precede — that material's editor). The cached entry stays shown while it
    // is read again; a failure keeps it (or the spinner).
    const activeQualityVersion = rowVersion(library, activeQualityMaterialId);
    // biome-ignore lint/correctness/useExhaustiveDependencies: activeQualityVersion IS the re-read signal; setPolicyCache is a React-stable setter.
    useEffect(() => {
        if (activeQualityMaterialId === null) return;
        const materialId = activeQualityMaterialId;
        let cancelled = false;
        const run = async () => {
            const r = await ports.evaluateMaterialPolicy(
                materialId,
                Core.Policy.Boundary.write(),
            );
            if (cancelled || !Result.isOk(r)) return;
            setPolicyCache((prev) => {
                const next = new Map(prev);
                next.set(materialId, r.value);
                return next;
            });
        };
        void run();
        return () => {
            cancelled = true;
        };
    }, [ports, activeQualityMaterialId, activeQualityVersion]);
};
